import { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Avatar, Chip, InputBase, Button,
  ToggleButtonGroup, ToggleButton, CircularProgress, Link, Snackbar, Alert
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';

import adminAxios from '../../utils/adminAxios';
import { urlImg } from '../../components/common/helpers';

const STATUS = {
  0: { label: 'Sin capturar', bg: 'sutil', color: 'text.secondary' },
  1: { label: 'Pendiente', bg: (t) => alpha(t.palette.warning.main, 0.3), color: 'warning.oscuro' },
  2: { label: 'Aprobado', bg: (t) => alpha(t.palette.success.main, 0.2), color: 'success.oscuro' },
  3: { label: 'Rechazado', bg: (t) => alpha(t.palette.error.main, 0.25), color: 'error.oscuro' },
};

const formatoFecha = (f) => f ? new Date(f).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const avatarUrl = (u, size = 80) => {
  const a = u?.avatar;
  if (!a) return undefined;
  if (a.startsWith('//')) return `https:${a}`;
  if (a.startsWith('http')) return a;
  return `${urlImg}/w_${size},h_${size},c_fill/users/${u._id}/${a}`;
};

const docUrl = (userId, archivo) => (archivo ? `${urlImg}/users/${userId}/${archivo}` : '');
const esPdf = (archivo) => archivo && archivo.toLowerCase().endsWith('.pdf');
const esImagen = (archivo) => archivo && /\.(jpe?g|png|webp|gif)$/i.test(archivo);
// Los documentos nuevos son PRIVADOS: el servidor manda `url` (firmada) y, si es imagen, `miniatura`. Con un servidor anterior (sin esos
// campos) se arma la URL pública de siempre. `tipoArchivo` dice qué es aunque el nombre no tenga extensión.
const enlaceDoc = (userId, doc) => doc.url || docUrl(userId, doc.archivo);
const imagenDoc = (userId, doc) => doc.miniatura || enlaceDoc(userId, doc);
const docEsPdf = (doc) => (doc.tipoArchivo ? doc.tipoArchivo === 'pdf' : esPdf(doc.archivo));
const docEsImagen = (doc) => (doc.tipoArchivo ? doc.tipoArchivo === 'imagen' : esImagen(doc.archivo));

const ChipStatus = ({ status }) => {
  const s = STATUS[status] || STATUS[0];
  return <Chip label={s.label} size="small" sx={{ height: 22, bgcolor: s.bg, color: s.color, fontWeight: 700 }} />;
};

const Verificaciones = () => {
  const [items, setItems] = useState([]);
  const [totalPendientes, setTotalPendientes] = useState(0);
  const [estado, setEstado] = useState('todos');
  const [q, setQ] = useState('');
  const [cargandoLista, setCargandoLista] = useState(true);

  const [selId, setSelId] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);

  const [accionando, setAccionando] = useState(false);
  const [toast, setToast] = useState(null);

  const cargarLista = useCallback(async () => {
    setCargandoLista(true);
    try {
      const res = await adminAxios.get('/api/admin/verificaciones', { params: { estado, q } });
      setItems(res.data.items);
      setTotalPendientes(res.data.totalPendientes);
      setSelId(prev => (res.data.items.some(i => i._id === prev) ? prev : res.data.items[0]?._id || null));
    } catch (e) {
      console.error('Error lista verificaciones', e.message);
    } finally {
      setCargandoLista(false);
    }
  }, [estado, q]);

  useEffect(() => {
    const t = setTimeout(cargarLista, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [cargarLista, q]);

  useEffect(() => {
    if (!selId) { setDetalle(null); return; }
    setCargandoDetalle(true);
    adminAxios.get(`/api/admin/verificaciones/${selId}`)
      .then(res => { setDetalle(res.data); })
      .catch(e => console.error('Error detalle', e.message))
      .finally(() => setCargandoDetalle(false));
  }, [selId]);

  const revisar = async (tipo, docId, accion) => {
    if (!detalle) return;
    setAccionando(true);
    try {
      await adminAxios.patch(`/api/admin/verificaciones/${detalle._id}`, { tipo, docId, accion });
      setToast({ tipo: 'success', msg: `${accion === 'aprobar' ? 'Aprobado' : 'Rechazado'} correctamente.` });
      const res = await adminAxios.get(`/api/admin/verificaciones/${detalle._id}`);
      setDetalle(res.data);
      cargarLista();
    } catch (e) {
      setToast({ tipo: 'error', msg: 'No se pudo completar la acción.' });
    } finally {
      setAccionando(false);
    }
  };

  // Botones aprobar/rechazar reutilizables.
  const Acciones = ({ status, onAprobar, onRechazar }) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button size="small" variant="outlined" startIcon={<CloseOutlinedIcon />} disabled={accionando || status === 3}
        onClick={onRechazar} sx={{ borderColor: 'divider', color: 'error.oscuro', textTransform: 'none' }}>Rechazar</Button>
      <Button size="small" variant="contained" startIcon={<CheckCircleOutlineOutlinedIcon />} disabled={accionando || status === 2}
        onClick={onAprobar} sx={{ bgcolor: 'primary.main', textTransform: 'none' }}>Aprobar</Button>
    </Box>
  );

  // Fila de dato de texto con su revisión (RFC / CURP).
  const FilaTexto = ({ label, tipo, dato }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', py: 1.5, borderBottom: 1, borderColor: 'sutil' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
        <Typography sx={{ color: 'primary.main', fontWeight: 600 }}>{dato.valor || 'No capturado'}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ChipStatus status={dato.status} />
        {dato.valor && <Acciones status={dato.status} onAprobar={() => revisar(tipo, null, 'aprobar')} onRechazar={() => revisar(tipo, null, 'rechazar')} />}
      </Box>
    </Box>
  );

  // Tarjeta de documento (título / certificado / otro).
  const DocCard = ({ tipo, doc }) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'sutil' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        {docEsImagen(doc) ? (
          <Box component="img" src={imagenDoc(detalle._id, doc)} alt={doc.nombre}
            sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5, border: 1, borderColor: 'sutil' }} />
        ) : (
          <Avatar variant="rounded" sx={{ bgcolor: 'grey.50', color: docEsPdf(doc) ? 'error.oscuro' : 'text.secondary', width: 56, height: 56 }}>
            {docEsPdf(doc) ? <PictureAsPdfOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}
          </Avatar>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 600, color: 'primary.main' }}>{doc.nombre || doc.archivo}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <ChipStatus status={doc.status} />
            <Link href={enlaceDoc(detalle._id, doc)} target="_blank" rel="noopener" sx={{ fontWeight: 600, fontSize: 13 }}>Ver documento</Link>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Acciones status={doc.status} onAprobar={() => revisar(tipo, doc._id, 'aprobar')} onRechazar={() => revisar(tipo, doc._id, 'rechazar')} />
      </Box>
    </Paper>
  );

  // Tarjeta de cédula profesional (con categoría y número).
  const CedulaCard = ({ doc }) => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'sutil' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
        {docEsImagen(doc) ? (
          <Box component="img" src={imagenDoc(detalle._id, doc)} alt={doc.nombre}
            sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1.5, border: 1, borderColor: 'sutil' }} />
        ) : (
          <Avatar variant="rounded" sx={{ bgcolor: 'grey.50', color: docEsPdf(doc) ? 'error.oscuro' : 'text.secondary', width: 56, height: 56 }}>
            {docEsPdf(doc) ? <PictureAsPdfOutlinedIcon /> : <WorkspacePremiumOutlinedIcon />}
          </Avatar>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 700, color: 'primary.main' }}>{doc.categoriaNombre || 'Cédula profesional'}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {doc.numero ? `N° ${doc.numero}` : 'Sin número'}{doc.nombre ? ` · ${doc.nombre}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <ChipStatus status={doc.status} />
            <Link href={enlaceDoc(detalle._id, doc)} target="_blank" rel="noopener" sx={{ fontWeight: 600, fontSize: 13 }}>Ver documento</Link>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Acciones status={doc.status} onAprobar={() => revisar('cedulas', doc._id, 'aprobar')} onRechazar={() => revisar('cedulas', doc._id, 'rechazar')} />
      </Box>
    </Paper>
  );

  const SeccionDocs = ({ titulo, tipo, docs }) => (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase', mb: 1.5 }}>{titulo}</Typography>
      {(!docs || docs.length === 0) ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin documentos.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {docs.map((d) => <DocCard key={d._id} tipo={tipo} doc={d} />)}
        </Box>
      )}
    </Box>
  );

  const ver = detalle?.verificacion;
  const redesActivas = detalle ? Object.entries(detalle.redes || {}).filter(([, v]) => v) : [];

  return (
    <Box>
      <Grid container spacing={3}>
        {/* ---------- Lista ---------- */}
        <Grid size={{ xs: 12, md: 5, lg: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'white', border: 1, borderColor: 'sutil', borderRadius: 3, px: 2, py: 1, mb: 2 }}>
            <SearchOutlinedIcon sx={{ color: 'text.secondary' }} fontSize="small" />
            <InputBase placeholder="Buscar usuario..." value={q} onChange={e => setQ(e.target.value)} sx={{ fontSize: 14, flex: 1 }} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {totalPendientes} pendiente{totalPendientes === 1 ? '' : 's'} de revisión
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={estado} onChange={(e, v) => v && setEstado(v)}>
              <ToggleButton value="pendiente" sx={{ textTransform: 'none', px: 1.5 }}>Pendientes</ToggleButton>
              <ToggleButton value="revisado" sx={{ textTransform: 'none', px: 1.5 }}>Revisadas</ToggleButton>
              <ToggleButton value="todos" sx={{ textTransform: 'none', px: 1.5 }}>Todas</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {cargandoLista ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : items.length === 0 ? (
            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', color: 'text.secondary' }}>Sin solicitudes.</Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {items.map((it) => {
                const sel = it._id === selId;
                return (
                  <Paper key={it._id} onClick={() => setSelId(it._id)}
                    sx={{
                      p: 2, borderRadius: 3, cursor: 'pointer', transition: 'all .15s',
                      border: sel ? '2px solid' : '1px solid', borderColor: sel ? 'secondary.main' : 'sutil',
                      boxShadow: sel ? ((t) => `0 4px 14px ${alpha(t.palette.secondary.main, 0.18)}`) : 'none',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                      <Avatar src={avatarUrl(it)} sx={{ width: 44, height: 44 }} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 700, color: 'primary.main' }}>{it.name} {it.apellido}</Typography>
                        <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>{it.email}</Typography>
                      </Box>
                      <Chip label={it.pendiente ? 'Pendiente' : 'Revisado'} size="small"
                        sx={{ height: 22, bgcolor: it.pendiente ? ((t) => alpha(t.palette.warning.main, 0.3)) : ((t) => alpha(t.palette.secondary.main, 0.15)), color: it.pendiente ? 'warning.oscuro' : 'primary.main', fontWeight: 700 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label={it.tipo} size="small"
                        sx={{ height: 20, fontWeight: 700, bgcolor: it.tipo === 'Facilitador' ? ((t) => alpha(t.palette.success.main, 0.2)) : ((t) => alpha(t.palette.secondary.main, 0.15)), color: it.tipo === 'Facilitador' ? 'success.oscuro' : 'primary.main' }} />
                      <Typography variant="caption" noWrap sx={{ color: 'text.secondary', flexGrow: 1 }}>{it.negocio || '—'}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: 'secondary.main' }}>
                        <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>Ver</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                      <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption">Solicitado el {formatoFecha(it.date)}</Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Grid>

        {/* ---------- Detalle ---------- */}
        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          {cargandoDetalle ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
          ) : !detalle ? (
            <Paper sx={{ p: 8, borderRadius: 3, textAlign: 'center', color: 'text.secondary' }}>
              Selecciona una solicitud para ver el detalle.
            </Paper>
          ) : (
            <>
              {/* Header detalle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Avatar src={avatarUrl(detalle, 120)} sx={{ width: 56, height: 56 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>{detalle.nombre}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={detalle.tipo} size="small"
                      sx={{ height: 20, fontWeight: 700, bgcolor: detalle.tipo === 'Facilitador' ? ((t) => alpha(t.palette.success.main, 0.2)) : ((t) => alpha(t.palette.secondary.main, 0.15)), color: detalle.tipo === 'Facilitador' ? 'success.oscuro' : 'primary.main' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{detalle.negocio || ''} · {detalle.email}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Resumen */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[['Solicitud', formatoFecha(detalle.date)], ['Rol asignado', detalle.tipo], ['Negocio', detalle.negocio || '—']].map(([l, v]) => (
                  <Grid size={{ xs: 12, sm: 4 }} key={l}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>{l}</Typography>
                    <Typography sx={{ color: 'primary.main', fontWeight: 500 }}>{v}</Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Datos del usuario + RFC/CURP */}
              <Paper sx={{ p: 3, borderRadius: 3, mb: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonOutlineOutlinedIcon sx={{ color: 'secondary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>Datos del Usuario</Typography>
                </Box>
                <Grid container columnSpacing={4} sx={{ mb: 1 }}>
                  {[['Nombre completo', detalle.nombre], ['Edad', detalle.edad ? `${detalle.edad} años` : '—'],
                  ['Sexo', detalle.sexo], ['Dirección', detalle.direccion], ['Teléfono', detalle.tel ? `${detalle.tel}${detalle.telVerificado === false ? ' (sin confirmar)' : ''}` : detalle.tel], ['Rol profesional', detalle.rolProfesional]].map(([l, v]) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={l} sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase' }}>{l}</Typography>
                      <Typography sx={{ color: 'primary.main', fontWeight: 500 }}>{v || '—'}</Typography>
                    </Grid>
                  ))}
                </Grid>

                {/* RFC y CURP (texto) con revisión */}
                <Box sx={{ mt: 1 }}>
                  <FilaTexto label="RFC" tipo="rfc" dato={ver.rfc} />
                  <FilaTexto label="CURP" tipo="curp" dato={ver.curp} />
                </Box>

                {detalle.descripcion && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>Sobre el usuario</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{detalle.descripcion}</Typography>
                  </Box>
                )}
                {redesActivas.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase', mb: 1 }}>Redes sociales</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {redesActivas.map(([red, val]) => <Chip key={red} label={`${red}: ${val}`} size="small" variant="outlined" sx={{ borderColor: 'divider' }} />)}
                    </Box>
                  </Box>
                )}
              </Paper>

              {/* Documentos */}
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                  <DescriptionOutlinedIcon sx={{ color: 'secondary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>Documentos adjuntos</Typography>
                </Box>
                {/* Cédulas profesionales (con categoría y número) */}
                {(ver.cedulas && ver.cedulas.length > 0) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, color: 'text.secondary', textTransform: 'uppercase', mb: 1.5 }}>Cédulas profesionales</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {ver.cedulas.map((d) => <CedulaCard key={d._id} doc={d} />)}
                    </Box>
                  </Box>
                )}
                <SeccionDocs titulo="Títulos profesionales" tipo="titulos" docs={ver.titulos} />
                <SeccionDocs titulo="Certificados" tipo="certificados" docs={ver.certificados} />
                <SeccionDocs titulo="Otros documentos" tipo="otros" docs={ver.otros} />
              </Paper>
            </>
          )}
        </Grid>
      </Grid>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.tipo} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
};

export default Verificaciones;
